import { darken, mix, rgba } from "polished";
import React from "react";
import styled, { css } from "styled-components";

import NoiseBkgLight from "../../static/image/bkg-noise-light.png";
import NoiseBkg from "../../static/image/bkg-noise.png";

export const BackgroundGrid: React.FunctionComponent<{
  visible?: boolean;
  className?: string;
}> = React.memo(function BackgroundGrid({ className, visible = true }) {
  return (
    <GridOuter className={className} visible={visible}>
      <TopFade>
        <CartesianMask>
          <CircleMask>
            <CircleGradient />
          </CircleMask>
        </CartesianMask>
      </TopFade>
    </GridOuter>
  );
});

const paperCss = css`
  background-image: url(${NoiseBkg});
  background-size: 200px 200px;
  opacity: 0.0625;
  pointer-events: none;

  ${({ theme }) =>
    theme.type === "LIGHT" &&
    css`
      background-image: url(${NoiseBkgLight});
      background-size: 256px 256px;
      opacity: 0.0325;
      pointer-events: none;
    `}
`;

export const BackgroundPaper = styled.div`
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    ${paperCss}
  }
`;

export const BackgroundDiagonals = styled.div<{
  $patternOpacity?: number;
}>`
  position: relative;
  background-color: ${({ theme }) =>
    darken(0.0125, theme.backgroundColor.DEFAULT)};

  background-image: ${({ $patternOpacity = 1, theme }) => {
    const hatchColor =
      theme.type === "DARK"
        ? rgba(theme.fontColor.BRAND, 0.077 * $patternOpacity)
        : rgba(theme.fontColor.BRAND, 0.06 * $patternOpacity);

    return `repeating-linear-gradient(
      45deg,
      transparent,
      transparent 4px,
      ${hatchColor} 4px,
      ${hatchColor} 5px
    )`;
  }};

  ${({ theme }) =>
    theme.type === "DARK" &&
    css`
      background-color: ${mix(
        0.98,
        theme.backgroundColor.DARK,
        theme.fontColor.BRAND,
      )};
    `}

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    ${paperCss}
  }
`;

export const BackgroundCheck = styled.div`
  position: relative;
  background-color: ${({ theme }) =>
    theme.type === "DARK"
      ? mix(0.99, theme.backgroundColor.DARK, theme.fontColor.BRAND)
      : mix(0.99, theme.backgroundColor.DEFAULT, theme.fontColor.BRAND)};
  background-size: 4px 4px;
  background-image: ${({ theme }) => {
    const checkeredColor =
      theme.type === "DARK"
        ? rgba(theme.fontColor.BRAND, 0.06)
        : rgba(theme.fontColor.BRAND, 0.06);
    return `conic-gradient(${checkeredColor}, ${checkeredColor} 25%, transparent 25%, transparent 50%, ${checkeredColor} 50%, ${checkeredColor} 75%, transparent 75%, transparent 100%)`;
  }};

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    ${paperCss}
  }
`;

const GridOuter = styled.div<{ visible: boolean }>`
  transition: opacity 0.2s;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  position: absolute;
  inset: 0;
  background-color: ${({ theme }) => theme.backgroundColor.DEFAULT};
  background-image: ${({ theme }) => {
    const stop1 = theme.backgroundColor.DARK;
    const stop2 = darken(0.026, theme.backgroundColor.DARK);
    return `linear-gradient(135deg, ${stop2} -30%, ${stop1}, ${stop2} 110%)`;
  }};
  overflow: clip;

  ${({ theme }) =>
    theme.type === "LIGHT" &&
    css`
      background-image: ${() => {
        const stop1 = theme.backgroundColor.DEFAULT;
        const stop2 = darken(0.06, theme.backgroundColor.DEFAULT);
        return `linear-gradient(135deg, ${stop1} -30%, ${stop1} 40%, ${stop2} 190%)`;
      }};
    `}

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    ${paperCss}
  }
`;
const TopFade = styled.div`
  position: absolute;
  inset: 0;
  mask-image: radial-gradient(circle at 66% 1800px, red 1000px, transparent);
`;

const CartesianMask = styled.div`
  position: absolute;
  inset: 0;
  background-color: ${({ theme }) => rgba(theme.borderColor.DEFAULT, 0.2)};
  mask-image:
    linear-gradient(
      to right,
      transparent,
      transparent calc(100% - 1px),
      rgba(0 0 0 / 0.4) calc(100% - 1px),
      rgba(0 0 0 / 0.4)
    ),
    linear-gradient(
      rgba(0 0 0 / 0.8),
      rgba(0 0 0 / 0.8) 1px,
      transparent 1px,
      transparent
    );
  mask-size: 32px 32px;
  mask-position: top;
`;

const CircleMask = styled.div`
  position: absolute;
  inset: 0;
`;

const CircleGradient = styled.div`
  position: absolute;
  inset: 0;
  aspect-ratio: 1 / 1;
  border-radius: 9999px;
  width: 1400px;
  background-color: ${({ theme }) => rgba(theme.intent.PRIMARY, 0.32)};
  filter: blur(200px);
  transform: translate(-50%, -260px);
  left: 50%;
  top: 100%;

  ${({ theme }) =>
    theme.type === "LIGHT" &&
    css`
      width: 1600px;
      transform: translate(-50%, -260px);
      background-color: ${rgba(theme.fontColor.BRAND, 0.32)};
    `}
`;
